import { StripeAddressElementChangeEvent } from "@stripe/stripe-js";
import { createSelector } from "reselect";

import { OkResponse } from "fond/types";
import { Store } from "fond/types/store";
import { LineItem, Product, PurchaseSummary, SetupIntentClientSecretResponse, SubscriptionTier } from "fond/types/stripe";
import type {
  Customer,
  PaymentActionAutomatic,
  PaymentActionInvoice,
  PurchasePaymentDetails,
  SubscriptionPaymentDetails,
} from "fond/types/subscriptions";

import { apiSlice } from "./apiSlice";

type GetSubscriptionTiersResponse = {
  SubscriptionTiers: SubscriptionTier[];
};

type GetProductsResponse = {
  Products: Product[];
};

type GetSubscriptionPaymentDetailsResponse = SubscriptionPaymentDetails;

export type CreatePurchaseSummaryRequest = {
  LineItems: LineItem[];
  PromoCodes?: string[];
  Address?: StripeAddressElementChangeEvent["value"]["address"];
  AccountID: string;
};

export type CreateUpgradeSummaryRequest = {
  LineItems: LineItem[];
  PromoCodes?: string[];
  Address?: StripeAddressElementChangeEvent["value"]["address"];
  SubscriptionID: string;
};

export type CreateUpgradeRequest = CreateUpgradeSummaryRequest & PurchasePaymentDetails;

export type CreateProratedPurchaseSummaryRequest = {
  SubscriptionID: string;
} & Pick<CreatePurchaseSummaryRequest, "LineItems" | "PromoCodes" | "Address">;

type CreatePurchaseRequest = CreatePurchaseSummaryRequest & PurchasePaymentDetails;

export type CreateDataPackPurchaseSummaryRequest = {
  SubscriptionID: string;
} & Omit<CreatePurchaseSummaryRequest, "AccountID">;

export type CreateDataPackPurchaseRequest = {
  SubscriptionID: string;
} & Omit<CreatePurchaseRequest, "AccountID">;

export type CreateFuturePurchaseRequest = {
  SubscriptionID: string;
} & Omit<CreatePurchaseRequest, "AccountID" | "PaymentMethod" | "CollectionMethod" | "Address">;

export type CreatePurchaseResponse = {
  Invoice: string;
  HostedInvoiceUrl: string;
  Status: "paid" | "in_progress" | "error";
};

export type SuccessResponse = {
  Message: "OK";
};

export const stripeSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getClientSecret: build.query<SetupIntentClientSecretResponse, string>({
      query: (accountId) => ({
        url: "/v2/stripe/client-secret",
        params: { account_id: accountId },
      }),
      // We set the maximum retries for this endpoint to 5, as it is critical to get the client secret for the checkout process.
      // If it fails, we want to retry a few times before giving up.
      // As we are waiting for Stripe to process the account creation asynchronously.
      extraOptions: { maxRetries: 5 },
      transformResponse: (response: SetupIntentClientSecretResponse) => response,
    }),
    getClientPortalLink: build.mutation<{ Url: string }, { accountId: string; returnPath?: string }>({
      query: ({ accountId, returnPath = "/subscription/payment-updated" }) => ({
        url: `/v2/stripe/account-portal-link`,
        method: "POST",
        params: {
          account_id: accountId,
          return_url: `${window.location.origin}${returnPath}`,
        },
      }),
    }),
    getSubscriptionTiers: build.query<SubscriptionTier[], void>({
      query: () => "/v2/stripe/subscription-tiers",
      transformResponse: (response: GetSubscriptionTiersResponse) => response.SubscriptionTiers,
    }),
    getSubscriptionPaymentDetails: build.query<GetSubscriptionPaymentDetailsResponse, string>({
      query: (subscriptionID) => `/v2/stripe/account-subscriptions/${subscriptionID}/payment`,
    }),
    getProducts: build.query<Product[], void>({
      query: () => "/v2/stripe/products",
      transformResponse: (response: GetProductsResponse) => response.Products,
    }),
    getCustomer: build.query<Customer, string>({
      query: (accountId) => ({
        url: "/v2/stripe/customer",
        params: { account_id: accountId },
      }),
    }),
    createPurchaseSummary: build.mutation<PurchaseSummary, CreatePurchaseSummaryRequest>({
      query: (lineItems) => ({
        url: "/v2/stripe/purchase-summary",
        method: "POST",
        body: lineItems,
      }),
      transformErrorResponse: (response) => response.data,
    }),
    createDataPackPurchase: build.mutation<CreatePurchaseResponse, CreateDataPackPurchaseRequest>({
      query: ({ SubscriptionID, ...payload }) => ({
        url: `/v2/stripe/account-subscriptions/${SubscriptionID}/data-pack-purchase`,
        method: "POST",
        body: payload,
      }),
      transformErrorResponse: (response) => response.data,
    }),
    createDataPackPurchaseSummary: build.mutation<PurchaseSummary, CreateDataPackPurchaseSummaryRequest>({
      query: ({ SubscriptionID, ...lineItems }) => ({
        url: `/v2/stripe/account-subscriptions/${SubscriptionID}/data-pack-purchase-summary`,
        method: "POST",
        body: lineItems,
      }),
      transformErrorResponse: (response) => response.data,
    }),
    createUpgrade: build.mutation<CreatePurchaseResponse, CreateUpgradeRequest>({
      query: ({ SubscriptionID, ...body }) => ({
        url: `/v2/stripe/account-subscriptions/${SubscriptionID}/upgrade-purchase`,
        method: "POST",
        body,
      }),
      transformErrorResponse: (response) => response.data,
    }),
    createUpgradeSummary: build.mutation<PurchaseSummary, CreateUpgradeSummaryRequest>({
      query: ({ SubscriptionID, ...body }) => ({
        url: `/v2/stripe/account-subscriptions/${SubscriptionID}/upgrade-summary`,
        method: "POST",
        body,
      }),
      transformErrorResponse: (response) => response.data,
    }),
    createFuturePurchase: build.mutation<OkResponse, CreateFuturePurchaseRequest>({
      query: ({ SubscriptionID, ...purchase }) => ({
        url: `/v2/stripe/account-subscriptions/${SubscriptionID}/phase`,
        method: "POST",
        body: purchase,
      }),
      invalidatesTags: [{ type: "Subscription", id: "LIST" }],
    }),
    updateSubscriptionPaymentDetails: build.mutation<OkResponse, { SubscriptionID: string } & (PaymentActionAutomatic | PaymentActionInvoice)>({
      query: ({ SubscriptionID, ...Payload }) => ({
        url: `/v2/stripe/account-subscriptions/${SubscriptionID}`,
        method: "PATCH",
        body: Payload,
      }),
      invalidatesTags: [{ type: "Subscription", id: "LIST" }],
    }),
    cancelSubscription: build.mutation<OkResponse, { SubscriptionID: string; Action: "cancel" | "uncancel" }>({
      query: ({ SubscriptionID, Action }) => ({
        url: `/v2/stripe/account-subscriptions/${SubscriptionID}`,
        method: "PATCH",
        body: { ActionType: Action },
      }),
      invalidatesTags: [{ type: "Subscription", id: "LIST" }],
    }),
    deleteFuturePurchaseSummary: build.mutation<OkResponse, { SubscriptionID: string }>({
      query: ({ SubscriptionID }) => ({
        url: `/v2/stripe/account-subscriptions/${SubscriptionID}/phase`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Subscription", id: "LIST" }],
    }),
    purchaseProducts: build.mutation<CreatePurchaseResponse, CreatePurchaseRequest>({
      query: (request) => ({
        url: "/v2/stripe/purchase-account-subscription",
        method: "POST",
        body: request,
      }),
      invalidatesTags: [{ type: "Subscription", id: "LIST" }],
    }),
  }),
});

export const {
  useCreateFuturePurchaseMutation,
  useCreatePurchaseSummaryMutation,
  useCreateUpgradeMutation,
  useCreateUpgradeSummaryMutation,
  useDeleteFuturePurchaseSummaryMutation,
  useCancelSubscriptionMutation,
  useUpdateSubscriptionPaymentDetailsMutation,
  useGetClientSecretQuery,
  useGetClientPortalLinkMutation,
  useGetCustomerQuery,
  useGetSubscriptionTiersQuery,
  useGetSubscriptionPaymentDetailsQuery,
  useGetProductsQuery,
  usePurchaseProductsMutation,
  useCreateDataPackPurchaseMutation,
  useCreateDataPackPurchaseSummaryMutation,
} = stripeSlice;

/**
 * Selectors
 */
export const selectProducts = createSelector(stripeSlice.endpoints.getProducts.select(), (result) => result.data);

export const selectSubscriptionPaymentDetails = createSelector(
  [(state: Store) => state, (state: Store, subscriptionId: string | undefined) => subscriptionId],
  (state, subscriptionId): GetSubscriptionPaymentDetailsResponse | undefined => {
    if (!subscriptionId) return undefined;
    const result = stripeSlice.endpoints.getSubscriptionPaymentDetails.select(subscriptionId)(state);
    return result.data;
  }
);

export const selectPurchasableTiers = createSelector(stripeSlice.endpoints.getSubscriptionTiers.select(), (result) => {
  const { data } = result;
  return data?.filter((tier) => tier.TierLevel > 0);
});

export const selectPlannerTierById = createSelector([(state) => state, (state: Store, packageId: string) => packageId], (state: Store, packageId) => {
  const data = createSelector(stripeSlice.endpoints.getSubscriptionTiers.select(), (result) => result.data)(state);
  return data?.find(({ ID }) => ID === packageId);
});
